import { formatGel } from '@/utils/money';
import type { FinancialPositionTone } from '@/api/models';

/**
 * The presentation vocabulary. Not a second opinion about the business — the
 * server decides which zone a metric is in, and this only says what that looks
 * like on screen.
 */
export type OverviewTone = 'danger' | 'warning' | 'success';

/**
 * The server's zones, drawn. The assessment rail names the same three, so a card
 * is amber for exactly the reason the rail is: because the business is Stable on
 * that measure.
 */
export const TONE_BY_ZONE: Record<FinancialPositionTone, OverviewTone> = {
    STRAINED: 'danger',
    STABLE: 'warning',
    STRONG: 'success',
};

/** Text colour per tone — for a figure that carries its own verdict. */
export const TONE_TEXT: Record<OverviewTone, string> = {
    danger: 'text-danger',
    warning: 'text-warning',
    success: 'text-success',
};

/**
 * The verdict band's fill and text, per tone.
 *
 * The `*-soft` pair rather than an `Alert`: HeroUI's alert statuses colour the
 * indicator and the title and leave the box white, which made a danger verdict
 * and a success verdict render identically — the one thing these four cards
 * exist to tell apart. The soft tokens are the design system's own answer to
 * "a pale tinted block with the saturated brand colour as text", and
 * `index.css` fixes them to the same values in both themes on purpose, so the
 * pair stays readable in dark mode where a lone `bg-*-soft` would not.
 */
export const VERDICT_SURFACE: Record<OverviewTone | 'accent', string> = {
    danger: 'bg-danger-soft text-danger-soft-foreground',
    warning: 'bg-warning-soft text-warning-soft-foreground',
    success: 'bg-success-soft text-success-soft-foreground',
    accent: 'bg-accent-soft text-accent-soft-foreground',
};

/** Fill per tone — for a threshold bar's segments. */
export const TONE_FILL: Record<OverviewTone, string> = {
    danger: 'bg-danger-soft',
    warning: 'bg-warning-soft',
    success: 'bg-success-soft',
};

/**
 * A signed money delta: `+₾ 12,400.00` / `-₾ 9,400.00`.
 *
 * `formatGel` is still the only money write edge — this only decides whether a
 * `+` goes in front, because a delta of zero movement and a delta of +12,400
 * must not read the same.
 */
export const formatSignedGel = (tetri: number): string =>
    tetri > 0 ? `+${formatGel(tetri)}` : formatGel(tetri);

const ONE_DECIMAL_FORMATTER = new Intl.NumberFormat('en-US', {
    maximumFractionDigits: 1,
    minimumFractionDigits: 1,
});

/** A signed unitless delta, one decimal: `+0.3` / `-0.9`. Not money. */
export const formatSignedRatio = (value: number): string =>
    `${value > 0 ? '+' : ''}${ONE_DECIMAL_FORMATTER.format(value)}`;

/**
 * A ratio for display: `5.41`. Not money — it is a multiple, not an amount, so
 * it carries no currency symbol.
 *
 * Pinned to `en-US` for the same reason `formatGel` is: a browser resolving to a
 * comma-decimal locale would otherwise print `5,41` beside `₾ 5.41` (BOS-162).
 */
const RATIO_FORMATTER = new Intl.NumberFormat('en-US', {
    maximumFractionDigits: 2,
    minimumFractionDigits: 2,
});

export const formatRatio = (value: number): string =>
    RATIO_FORMATTER.format(value);
